import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CreditCard, Search } from "lucide-react";
import Seo from "../components/Seo";
import { lookupBooking } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { money } from "../lib/format";
import { LOCATIONS } from "../lib/content";

const WB = LOCATIONS["west-branch"];

export default function BookingLookup() {
  const { signedIn } = useAuth();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [state, setState] = useState({ loading: false, booking: null, error: null });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, booking: null, error: null });
    try {
      const res = await lookupBooking(email.trim(), code.trim());
      setState({ loading: false, booking: res.booking, error: null });
    } catch (err) {
      const expired = err.payload?.code === "guest_session_expired";
      setState({
        loading: false,
        booking: null,
        error: expired
          ? "Your secure lookup session expired. Please enter your email and confirmation code again."
          : err.payload?.userMessage || err.message,
      });
    }
  };

  const booking = state.booking;
  const outstanding = booking?.outstanding ?? 0;

  return (
    <>
      <Seo
        title="Find Your Reservation | West Branch Marina"
        description="Look up your boat reservation with your email address and order confirmation code."
        path="/booking/lookup"
      />

      <div className="bg-navy pt-24 pb-10 lg:pt-32">
        <div className="mx-auto max-w-3xl px-5 lg:px-10">
          <p className="eyebrow text-lake">Reservations</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Find your reservation
          </h1>
          <p className="mt-4 text-base text-white/85">
            Enter the email you booked with and your <strong className="font-bold">order</strong> confirmation code —
            not an individual boat's reservation code.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-5 py-12 lg:px-10">
        {/* A signed-in customer already owns their orders: the vendor accepts our
            customer token on order-scoped calls, so no confirmation code is needed. */}
        {signedIn && (
          <div data-testid="lookup-signed-in-shortcut" className="mb-8 rounded-2xl border border-lake/40 bg-lake/[0.07] p-4 text-sm text-navy">
            You're signed in — your trips, codes and balances are already on{" "}
            <Link to="/account/trips" data-testid="lookup-trips-link" className="font-bold text-lake underline">
              My trips
            </Link>
            , and you can pay a balance there without a confirmation code.
          </div>
        )}
        <form onSubmit={submit} data-testid="lookup-form" className="rounded-3xl border border-sand-border bg-white p-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="lk-email" className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                Email Address
              </label>
              <input
                id="lk-email"
                data-testid="lookup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 w-full rounded-xl border border-sand-border bg-sand px-3 text-sm text-navy"
              />
            </div>
            <div>
              <label htmlFor="lk-code" className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                Order Confirmation Code
              </label>
              <input
                id="lk-code"
                data-testid="lookup-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="mt-2 h-12 w-full rounded-xl border border-sand-border bg-sand px-3 text-sm text-navy"
              />
            </div>
          </div>
          <button
            type="submit"
            data-testid="lookup-submit"
            disabled={state.loading}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover disabled:bg-slate-400"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            {state.loading ? "Looking…" : "Find my reservation"}
          </button>
        </form>

        {state.error && (
          <div data-testid="lookup-error" role="alert" className="mt-6 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-700" aria-hidden="true" />
            <p className="text-sm text-red-900">
              {state.error}{" "}
              <a href={WB.phoneHref} className="font-bold underline">Or call {WB.phone}</a>
            </p>
          </div>
        )}

        {state.booking && (
          <div data-testid="lookup-result" className="mt-8 rounded-3xl border border-sand-border bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Order confirmation code</p>
            <p data-testid="lookup-order-code" className="mt-1 font-display text-2xl font-bold text-navy">
              {state.booking.orderConfirmationCode}
            </p>
            {state.booking.status && (
              <p className="mt-2 text-sm text-slate-600">Status: <strong className="font-bold text-navy">{state.booking.status}</strong></p>
            )}
            {typeof state.booking.orderTotal === "number" && (
              <p data-testid="lookup-total" className="mt-1 text-sm text-slate-600">
                Total: <strong className="font-bold text-navy">{money(state.booking.orderTotal)}</strong>
              </p>
            )}
            {outstanding > 0 && (
              <div data-testid="lookup-balance-panel" className="mt-5 rounded-2xl border border-lake/40 bg-lake/[0.07] p-4">
                <p className="text-sm text-navy">
                  <strong className="font-bold">{money(outstanding)}</strong> is still outstanding on this
                  order.
                </p>
                <Link
                  to={`/booking/payment?code=${encodeURIComponent(state.booking.orderConfirmationCode)}`}
                  data-testid="lookup-pay-balance"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-bold text-white hover:bg-ember-hover"
                >
                  <CreditCard className="h-4 w-4" aria-hidden="true" />
                  Pay {money(outstanding)} now
                </Link>
                <p className="mt-3 text-xs text-slate-600">
                  Prefer to settle up in person? The balance can also be paid at the marina when you check in.
                </p>
              </div>
            )}
            <ul className="mt-5 space-y-3">
              {(state.booking.lines || []).map((l) => (
                <li key={l.lineNo ?? l.reservationId} data-testid={`lookup-line-${l.lineNo ?? l.reservationId}`} className="rounded-2xl bg-sand p-4 text-sm">
                  <p className="font-display font-bold text-navy">{l.displayName || `Boat ${l.lineNo}`}</p>
                  <p className="mt-1 text-slate-600">Reservation code: {l.reservationConfirmationCode}</p>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-slate-500">
              For your security this lookup session lasts 20 minutes, then you'll need to enter your details again.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
